import React, { useState } from "react";
import {
  ChevronDown,
  BadgeInfo,
  User,
  Loader,
  Gift,
  XCircle,
  CheckCircle,
  DollarSign,
  Calendar,
  Clock,
  Link as LinkIcon,
  MessageSquare,
  ChevronRight,
  X,
  MessageCircle,
  ExternalLink,
  Check,
  AlertCircle,
  Maximize2,
  Minimize2,
  Code,
} from "lucide-react";

import { taskAPI } from "../../../services/api";
import TaskApplicationForm from "../task-components/TaskApplicationForm";
import TaskApplicationsView from "../task-components/TaskApplicationsView";
import TaskSubmissionForm from "../task-components/TaskSubmissionForm";
import TaskSubmissionsView from "../task-components/TaskSubmissionsView";
import ReleaseIUModal from "../task-components/ReleaseIUModal";
import {
  Drawer,
  PERMISSION_TYPES,
  STATUS_STYLES,
  StatusChip,
} from "../kanban-board";
import { formatDate } from "../../../utils/formatDate";
import { useContractServices } from "../../../context/ContractServicesContext";
import { toast } from "react-toastify";

import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/react";

export function TaskDetailModal({
  open,
  onClose,
  task,
  project,
  walletAddress,
  innovators,
  onTaskUpdated,
  onOpenApplication,
  onOpenSubmission,
  isCreator,
  onRespond,
  showSubmitForm,
  setShowSubmitForm,
  onReleaseIUs,
  appsOpen,
  setAppsOpen,
  subsOpen,
  setSubsOpen,
  appsModal,
  setAppsModal,
  subsModal,
  selectedApplication,
  setSelectedApplication,
  selectedSubmission,
  setSelectedSubmission,
  setSubsModal,
  showAppForm,
  setShowAppForm,
  getExplorerUrl,
}) {
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [processingApplicationId, setProcessingApplicationId] = useState(null);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseSubmission, setReleaseSubmission] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { services, isReady } = useContractServices();

  if (!task) return null;
  
  // Check if wallet is connected (empty string means no wallet)
  const isWalletConnected = walletAddress && walletAddress !== "";
  
  const isAssignee = isWalletConnected &&
    task.assigneeWalletAddress?.toLowerCase() === walletAddress.toLowerCase();

  // For application type tasks, check if current user is the person with approved application
  const hasApprovedApplication = isWalletConnected && 
    task.permissionType === "application" && 
    task.applicants?.some(app => 
      app.contributorWalletAddress?.toLowerCase() === walletAddress.toLowerCase() && 
      app.status === "approved"
    );

  // Update canSubmit logic to allow submissions while task is in submitted status
  // for "multiple" type tasks, and for application tasks when user has an approved application
  const canSubmit = isWalletConnected && (
    // For multiple/open submissions type tasks
    (task.permissionType === "multiple" && ["open", "submitted"].includes(task.status)) ||
    
    // For application type tasks - when user has an approved application
    (task.permissionType === "application" && hasApprovedApplication && ["in_progress"].includes(task.status)) ||
    
    // For assign and claim type tasks - when user is the assignee
    (["assign", "claim"].includes(task.permissionType) && isAssignee && ["open", "in_progress"].includes(task.status))
  );

  /* ───────── primary CTA selector ───────── */
  let primaryAction = null;

  // Wallet Connection CTA
  if (!isWalletConnected) {
    primaryAction = (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <p className="text-amber-800 text-sm">Connect your wallet to interact with this task.</p>
      </div>
    );
  }
  // Claim
  else if (
    task.permissionType === "claim" &&
    task.status === "open" &&
    !task.assigneeWalletAddress
  ) {
    primaryAction = (
      <button
        onClick={async () => {
          try {
            const { data } = await taskAPI.claimTask(task._id, {
              walletAddress,
            });
            
            // Automatically move task to in_progress when claimed
            if (data.status !== "in_progress") {
              await taskAPI.update(task._id, { status: "in_progress" });
            }
            
            onTaskUpdated(data);
            onClose();
          } catch (error) {
            console.error("Error claiming task:", error);
          }
        }}
        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
      >
        Claim Task
      </button>
    );
  }

  // Apply (non‑creator, not yet applied)
  else if (
    task.permissionType === "application" &&
    task.status === "open" &&
    !isCreator &&
    !task.applicants?.some(
      (a) =>
        a.contributorWalletAddress?.toLowerCase() ===
        walletAddress.toLowerCase()
    )
  ) {
    primaryAction = (
      <button
        onClick={() => setShowAppForm(true)}
        className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors shadow-sm"
      >
        Apply for Task
      </button>
    );
  }

  // Submit work
  else if (canSubmit && ["open", "in_progress", "submitted"].includes(task.status)) {
    // For task submission logic
    const handleSubmitWorkClick = () => {
      // IMPORTANT: For multiple/open submissions type tasks:
      // 1. When first submission occurs, task should go to "submitted" (not "in_progress")
      // 2. Task should stay in "submitted" so multiple users can submit work
      if (task.permissionType === "multiple" && task.status === "open") {
        // Change status directly to submitted for open to submissions tasks
        changeStatus("submitted")
          .then(() => {
            // Then open submission form
            setShowSubmitForm(true);
          })
          .catch(error => {
            console.error("Error updating task status:", error);
            // Still open submission form even if status update fails
            setShowSubmitForm(true);
          });
      } else if (task.permissionType === "application" && task.status === "in_progress") {
        // For application type tasks where an application is approved
        // Just open the submission form directly
        setShowSubmitForm(true);
      } else {
        // For other task types, just open submission form
        // (for non-multiple tasks, status changes in other places)
        setShowSubmitForm(true);
      }
    };

    primaryAction = (
      <button
        onClick={handleSubmitWorkClick}
        className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors shadow-sm"
      >
        Submit Work
      </button>
    );
  }

  /* ───────── editable selectors only for creator & Backlog|Open ───────── */
  const editable = isWalletConnected && isCreator;

  /* dropdown data */
  const statusOpts = [
    { id: "backlog", name: "Backlog" },
    { id: "open", name: "Open" },
    { id: "in_progress", name: "In Progress" },
    { id: "submitted", name: "Submitted" },
    { id: "approved", name: "Approved" },
    { id: "rejected", name: "Rejected" },
  ];
  const assigneeOpts = [
    ...innovators.map((inv) => ({
      id: inv.walletAddress,
      name: inv.name || inv.walletAddress.slice(0, 10),
      avatar: inv.avatar || "",
    })),
  ];

  /* API helpers */
  const changeStatus = async (next) => {
    // Only allow creators to change status
    if (!isCreator) return;
    
    // If changing from backlog to any other status, only creators can do this
    if (task.status === "backlog" && next !== "backlog" && !isCreator) {
      return;
    }
    
    const { data } = await taskAPI.update(task._id, { status: next });
    onTaskUpdated(data);
  };
  const changeAssignee = async (wallet) => {
    // Only allow creators to change assignee and only for "assign" type tasks
    if (!isCreator || task.permissionType !== "assign") return;
    
    // Only allow changing assignee when task is in backlog or open status
    if (!["backlog", "open"].includes(task.status)) return;
    
    const { data } = await taskAPI.update(task._id, { assignee: wallet });
    onTaskUpdated(data);
    
    // If this is an 'assign' type task and we're setting an assignee, 
    // automatically change status to in_progress
    if (task.permissionType === "assign" && wallet && task.status !== "in_progress") {
      changeStatus("in_progress");
    }
  };

  const handleApprove = async (applicationId) => {
    setLoading(true);
    setProcessingApplicationId(applicationId);
    try {
      await onRespond(applicationId, "approved", feedback);
      
      // Find the application that was just approved to get the wallet address
      const approvedApplication = task.applicants.find(app => app._id === applicationId);
      if (approvedApplication && approvedApplication.contributorWalletAddress) {
        // Set the task assignee to the approved applicant's wallet address
        await taskAPI.update(task._id, { 
          assignee: approvedApplication.contributorWalletAddress,
          status: "in_progress"  // Always move to in_progress when an application is approved
        });
      } else {
        // If we couldn't find the application, just update the status
        await changeStatus("in_progress");
      }
      
      setSelectedApplication(null);
      setFeedback("");
    } catch (error) {
      console.error("Error approving application:", error);
    } finally {
      setLoading(false);
      setProcessingApplicationId(null);
    }
  };

  const handleReject = async (applicationId) => {
    setLoading(true);
    setProcessingApplicationId(applicationId);
    try {
      await onRespond(applicationId, "rejected", feedback);
      setSelectedApplication(null);
      setFeedback("");
    } catch (error) {
      console.error("Error rejecting application:", error);
    } finally {
      setLoading(false);
      setProcessingApplicationId(null);
    }
  };

  // Helper function to get explorer URL with fallback
  const safeGetExplorerUrl = (txHash) => {
    if (typeof getExplorerUrl === 'function') {
      return getExplorerUrl(txHash);
    }
    // Default to Optimism Sepolia as fallback
    return `https://sepolia-optimism.etherscan.io/tx/${txHash}`;
  };

  const handleReleaseIUs = async (taskId, contributorId) => {
    // Find the submission that matches the contributorId
    const submission = task.submissions.find(
      sub => sub.contributor?._id === contributorId || sub.contributor === contributorId
    );
    
    if (submission) {
      // Show the ReleaseIUModal with the selected submission
      setReleaseSubmission(submission);
      setShowReleaseModal(true);
    } else {
      console.error("Submission not found for contributor ID:", contributorId);
      toast.error("Could not find submission details");
    }
  };

  // Handle after IUs have been released
  const handleIUsReleased = async (taskId, contributorId) => {
    try {
      // Fetch the updated task with iusReleased flag set
      const { data: updatedTask } = await taskAPI.getById(taskId);
      
      // Update the task in the UI to reflect the IU release status
      onTaskUpdated(updatedTask);
      
      // Close any modals that might be open
      setShowReleaseModal(false);
    } catch (error) {
      console.error("Error refreshing task after IU release:", error);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Drawer show={open} onClose={onClose} isFullscreen={isFullscreen}>
      {/* Header with close button and fullscreen toggle */}
      <div className="flex items-center justify-between mb-4 border-b border-gray-200 pb-4">
        <div className="flex items-center">
          <button
            onClick={toggleFullscreen}
            className="mr-2 p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? 
              <Minimize2 className="h-5 w-5" /> : 
              <Maximize2 className="h-5 w-5" />
            }
          </button>
          <h1 className="text-xl font-bold leading-tight text-gray-900">{task.title}</h1>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 focus:outline-none p-1 rounded-md hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Permission type banner */}
      {PERMISSION_TYPES[task.permissionType]?.banner && (
        <div className="mb-4 flex items-start rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800">
          <BadgeInfo className="mr-2 h-5 w-5 flex-shrink-0 text-indigo-600" />
          <p>{PERMISSION_TYPES[task.permissionType].banner}</p>
        </div>
      )}

      {/* Task details in a grid layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-700">
              <Loader className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium">Status</span>
            </div>
            {editable ? (
              <Listbox
                value={statusOpts.find((o) => o.id === task.status)}
                onChange={(opt) => changeStatus(opt.id)}
              >
                <div className="relative">
                  <ListboxButton
                    className={`flex w-[140px] items-center justify-between rounded-full px-3 py-1.5 text-left text-sm ${
                      STATUS_STYLES[task.status].bg
                    }`}
                  >
                    {({ value }) => {
                      return (
                        <>
                          <StatusChip status={value.id} />
                          <ChevronDown className="h-4 w-4 text-white" />
                        </>
                      );
                    }}
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 w-full rounded-lg bg-white text-sm shadow-xl ring-1 ring-black/5 focus:outline-none overflow-hidden">
                    {statusOpts.map((o) => (
                      <ListboxOption
                        key={o.id}
                        value={o}
                        className="cursor-pointer select-none px-3 py-2 hover:bg-indigo-50 data-focus:bg-indigo-600 data-focus:text-white"
                      >
                        {({ selected }) => (
                          <span className={selected ? "font-semibold" : ""}>
                            <StatusChip status={o.id} />
                          </span>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            ) : (
              <StatusChip status={task.status} />
            )}
          </div>
        </div>

        {/* Creator */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-700">
              <Code className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium">Task Creator</span>
            </div>
            <div className="text-gray-800 bg-gray-100 rounded-lg px-3 py-1.5">
              {task.createdBy && task.createdBy.walletAddress ? (
                <a 
                href={`/innovators/${task.createdBy.walletAddress}`}
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                >
                  {task.createdBy.walletAddress.slice(0, 8)}…
                </a>
              ) : (
                <span className="text-gray-500">Unknown</span>
              )}
            </div>
          </div>
        </div>

        {/* Assignee */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-700">
              <User className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium">Contributor</span>
              {/* Add permission type indicator */}
              {task.permissionType && (
                <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  {task.permissionType === "assign" && "Manually Assigned"}
                  {task.permissionType === "claim" && "Direct Claiming"}
                  {task.permissionType === "application" && "Application Based"}
                  {task.permissionType === "multiple" && "Multiple Contributors"}
                </span>
              )}
            </div>
            {/* Only make the assignee editable for "assign" type tasks when task is in backlog or open status */}
            {editable && task.permissionType === "assign" && ["backlog", "open"].includes(task.status) ? (
              <Listbox
                value={
                  assigneeOpts.find(
                    (o) => o.id === (task.assigneeWalletAddress || "")
                  ) || assigneeOpts[0]
                }
                onChange={(opt) => changeAssignee(opt.id)}
              >
                <div className="relative">
                  <ListboxButton className="flex w-full items-center justify-between rounded-lg border border-gray-300 px-3 py-1.5 bg-white text-left text-sm">
                    {({ value }) => (
                      <>
                        <span className="flex items-center gap-2 pr-6 text-gray-700">
                          {value.avatar && (
                            <img
                              src={value.avatar}
                              alt=""
                              className="h-5 w-5 rounded-full"
                            />
                          )}
                          {value.name}
                        </span>
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      </>
                    )}
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg bg-white text-sm shadow-xl ring-1 ring-black/5 focus:outline-none">
                    {assigneeOpts.map((o) => (
                      <ListboxOption
                        key={o.id}
                        value={o}
                        className="group relative cursor-pointer select-none py-2 px-3 text-gray-900 hover:bg-indigo-50 data-focus:bg-indigo-600 data-focus:text-white"
                      >
                        {({ selected }) => (
                          <span
                            className={`flex items-center gap-2 ${
                              selected ? "font-semibold" : ""
                            }`}
                          >
                            {o.avatar && (
                              <img
                                src={o.avatar}
                                alt=""
                                className="h-5 w-5 rounded-full"
                              />
                            )}
                            {o.name}
                          </span>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            ) : (
              // Display appropriate contributor info based on permission type
              <div className="text-gray-800 bg-gray-100 rounded-lg px-3 py-1.5">
                {task.permissionType === "assign" && task.assigneeWalletAddress ? (
                  // For assign type with assignee set
                  <a 
                    href={`/innovators/${task.assigneeWalletAddress}`}
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {task.assigneeWalletAddress.slice(0, 8)}…
                  </a>
                ) : task.permissionType === "claim" && task.assigneeWalletAddress ? (
                  // For claim type with assignee (someone claimed it)
                  <a 
                    href={`/innovators/${task.assigneeWalletAddress}`}
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {task.assigneeWalletAddress.slice(0, 8)}…
                  </a>
                ) : task.permissionType === "application" && task.assigneeWalletAddress ? (
                  // For application type with approved applicant
                  <a 
                    href={`/innovators/${task.assigneeWalletAddress}`}
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {task.assigneeWalletAddress.slice(0, 8)}…
                  </a>
                ) : task.permissionType === "multiple" && task.submissions?.length > 0 ? (
                  // For multiple submissions type, show count of contributors
                  <span>
                    {task.submissions.length} contributor{task.submissions.length !== 1 ? 's' : ''}
                  </span>
                ) : task.permissionType === "multiple" ? (
                  // For multiple submissions with no submissions yet
                  <span className="text-gray-500">Open to anyone</span>
                ) : task.permissionType === "application" && task.status === "open" ? (
                  // For application type with no approved applications yet
                  <span className="text-gray-500">Pending applications</span>
                ) : task.permissionType === "claim" && task.status === "open" ? (
                  // For unclaimed task
                  <span className="text-gray-500">Not yet claimed</span>
                ) : (
                  // Default fallback
                  <span className="text-gray-500">None</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Reward */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-gray-700">
              <Gift className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium">Reward</span>
            </div>
            <span className="text-green-600 font-medium bg-green-50 px-3 py-1 rounded-lg">{task.taskPoints} IU</span>
          </div>
        </div>

        {/* Due Date (if available) */}
        {task.dueDate && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center text-gray-700">
                <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                <span className="font-medium">Due Date</span>
              </div>
              <span className="text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-lg">
                {formatDate(task.dueDate)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
        <h2 className="text-md font-semibold mb-3 text-gray-800 flex items-center">
          <MessageCircle className="w-4 h-4 mr-2 text-gray-500" />
          Description
        </h2>
        <div className="prose prose-sm max-w-none text-gray-600 bg-gray-50 p-4 rounded-lg border border-gray-100">
          {task.description || "No description available."}
        </div>
      </div>

      {/* Primary action button */}
      {primaryAction && (
        <div className="mb-6 flex justify-center">
          {primaryAction}
        </div>
      )}

      {/* Show a message if this is an application task in progress but user is not the approved applicant */}
      {task.permissionType === "application" && 
       task.status === "in_progress" && 
       !hasApprovedApplication &&
       !isCreator && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium">Another application has been approved</p>
              <p className="text-sm mt-1">Only the approved applicant can submit work for this task.</p>
            </div>
          </div>
        </div>
      )}

      {/* Applications section - card-based layout */}
      {isCreator &&
        task.permissionType === "application" &&
        task.applicants?.length > 0 && (
          <section className="mb-6">
            <div 
              className="flex items-center justify-between mb-3 bg-indigo-50 p-3 rounded-lg cursor-pointer"
              onClick={() => setAppsOpen(!appsOpen)}
            >
              <h2 className="text-md font-semibold text-indigo-800 flex items-center">
                <MessageSquare className="w-4 h-4 mr-2" />
                Applications ({task.applicants.length})
              </h2>
              <ChevronDown 
                className={`h-5 w-5 text-indigo-600 transition-transform ${
                  appsOpen ? "rotate-180" : ""
                }`} 
              />
            </div>
            
            {appsOpen && (
              <div className="space-y-3">
                {/* Status banner when task has an approved application */}
                {task.status === "in_progress" && task.applicants.some(app => app.status === "approved") && (
                  <div className="bg-green-50 border border-green-200 p-3 rounded-lg flex items-center mb-4">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-green-800 font-medium">An application has been approved</p>
                      <p className="text-green-700 text-sm">This task is now in progress. Other applications can no longer be approved.</p>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {task.applicants?.map((application) => {
                    // Calculate status specific classes
                    const statusColors = {
                      approved: {
                        border: "border-green-200",
                        bg: "bg-green-50",
                        icon: "bg-green-100 text-green-600",
                        badgeBg: "bg-green-100",
                        badgeText: "text-green-800"
                      },
                      rejected: {
                        border: "border-red-200",
                        bg: "bg-red-50",
                        icon: "bg-red-100 text-red-600",
                        badgeBg: "bg-red-100",
                        badgeText: "text-red-800"
                      },
                      pending: {
                        border: "border-yellow-200",
                        bg: "bg-yellow-50",
                        icon: "bg-yellow-100 text-yellow-600",
                        badgeBg: "bg-yellow-100",
                        badgeText: "text-yellow-800"
                      }
                    };
                    
                    const colors = statusColors[application.status] || statusColors.pending;
                    
                    return (
                      <div 
                        key={application._id} 
                        className={`bg-white border ${colors.border} rounded-lg shadow-sm overflow-hidden transition-all hover:shadow-md hover:border-indigo-300 cursor-pointer`}
                        onClick={() => {
                          setSelectedApplication(application);
                          setAppsModal(true);
                        }}
                      >
                        <div className="flex items-center p-3">
                          {/* User icon and wallet */}
                          <div className="flex items-center flex-1 min-w-0">
                            <div className={`p-1.5 rounded-full mr-2 ${colors.icon}`}>
                              <User className="h-3.5 w-3.5" />
                            </div>
                            <span className="font-medium text-gray-800 truncate text-sm">
                              <a 
                                href={`/innovators/${application.contributorWalletAddress}`}
                                className="text-indigo-600 hover:text-indigo-800 hover:underline"
                                onClick={(e) => e.stopPropagation()} // Prevent triggering the parent onClick
                              >
                                {application.contributorWalletAddress?.slice(0, 6)}...{application.contributorWalletAddress?.slice(-4)}
                              </a>
                              
                              {/* Show "Assignee" badge if this is the approved application */}
                              {application.status === "approved" && (
                                <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                  Contributor
                                </span>
                              )}
                              
                              {/* Show "You" badge if this is the current user's application */}
                              {application.contributorWalletAddress.toLowerCase() === walletAddress.toLowerCase() && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                  You
                                </span>
                              )}
                            </span>
                          </div>
                          
                          {/* Status badge */}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${colors.badgeBg} ${colors.badgeText}`}>
                            {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                          </span>
                        </div>
                        
                        {/* Middle content - Time estimate */}
                        <div className="p-3 flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="h-3 w-3 mr-1" />
                            <span>{application.timeEstimate || "No time estimate"}</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="h-3 w-3 mr-1" />
                            <span>{new Date(application.applicationDate).toLocaleDateString()}</span>
                          </div>
                        </div>
                        
                        {/* Application actions - View/Review button */}
                        <div className="p-3 flex justify-between items-center border-t border-gray-100">
                          {/* Links indicator */}
                          {application.links && application.links.length > 0 && (
                            <div className="flex items-center text-xs text-gray-500">
                              <LinkIcon className="h-3 w-3 mr-1" />
                              <span>{application.links.length} {application.links.length === 1 ? 'link' : 'links'}</span>
                            </div>
                          )}
                          
                          <div
                            className={`text-xs flex items-center px-3 py-1.5 rounded-md ${
                              application.status === "pending"
                                ? "text-indigo-600 bg-indigo-50" 
                                : application.status === "approved"
                                ? "text-green-700 bg-green-50"
                                : "text-red-700 bg-red-50"
                            }`}
                          >
                            {application.status === "pending" ? (
                              <>
                                <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                                Review Application
                              </>
                            ) : application.status === "approved" ? (
                              <>
                                <Check className="h-3.5 w-3.5 mr-1.5" />
                                View Details
                              </>
                            ) : (
                              <>
                                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                                View Details
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

      {/* Submissions section - card-based layout */}
      {task.submissions?.length > 0 && (
        <section className="mb-6">
          <div 
            className="flex items-center justify-between mb-3 bg-indigo-50 p-3 rounded-lg cursor-pointer"
            onClick={() => setSubsOpen(!subsOpen)}
          >
            <h2 className="text-md font-semibold text-indigo-800 flex items-center">
              <MessageSquare className="w-4 h-4 mr-2" />
              Submissions ({task.submissions.length})
            </h2>
            <ChevronDown 
              className={`h-5 w-5 text-indigo-600 transition-transform ${
                subsOpen ? "rotate-180" : ""
              }`} 
            />
          </div>
          
          {subsOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {task.submissions.map((submission) => {
                // Calculate status specific classes
                const statusColors = {
                  approved: {
                    border: "border-green-200",
                    bg: "bg-green-50",
                    icon: "bg-green-100 text-green-600",
                    badgeBg: "bg-green-100",
                    badgeText: "text-green-800"
                  },
                  rejected: {
                    border: "border-red-200",
                    bg: "bg-red-50",
                    icon: "bg-red-100 text-red-600",
                    badgeBg: "bg-red-100",
                    badgeText: "text-red-800"
                  },
                  pending: {
                    border: "border-yellow-200",
                    bg: "bg-yellow-50",
                    icon: "bg-yellow-100 text-yellow-600",
                    badgeBg: "bg-yellow-100",
                    badgeText: "text-yellow-800"
                  }
                };
                
                const colors = statusColors[submission.status] || statusColors.pending;
                
                return (
                  <div 
                    key={submission._id} 
                    className={`bg-white border ${colors.border} rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all hover:border-indigo-300`}
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setSubsModal(true);
                    }}
                  >
                    <div className="flex items-center p-3">
                      {/* User icon and wallet */}
                      <div className="flex items-center flex-1 min-w-0">
                        <div className={`p-1.5 rounded-full mr-2 ${colors.icon}`}>
                          <User className="h-3.5 w-3.5" />
                        </div>
                        <span className="font-medium text-gray-800 truncate text-sm">
                          <a 
                            href={`/innovators/${submission.contributorWalletAddress}`}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline"
                            onClick={(e) => e.stopPropagation()} // Prevent triggering the parent onClick
                          >
                            {submission.contributorWalletAddress?.slice(0, 6)}...{submission.contributorWalletAddress?.slice(-4)}
                          </a>
                        </span>
                      </div>
                      
                      {/* Status badge */}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${colors.badgeBg} ${colors.badgeText}`}>
                        {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                      </span>
                    </div>
                    
                    {/* Divider */}
                    <div className="h-px bg-gray-200"></div>
                    
                    {/* Footer with metadata */}
                    <div className="p-3 flex items-center justify-between bg-gray-50">
                      <div className="flex items-center space-x-2">
                        {/* Date */}
                        <div className="flex items-center text-gray-500 text-xs">
                          <Calendar className="h-3 w-3 mr-1" />
                          <span className="whitespace-nowrap">{new Date(submission.submissionDate).toLocaleDateString()}</span>
                        </div>
                        
                        {/* Links count if any */}
                        {submission?.links?.length > 0 && (
                          <div className="flex items-center text-xs text-gray-500">
                            <LinkIcon className="h-3 w-3 mr-1 text-gray-400" />
                            <span>{submission.links.length}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* IUs released tag */}
                      {submission.status === "approved" && task.iusReleased && (
                        <span className="inline-flex items-center rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-800">
                          <DollarSign className="h-3 w-3 mr-0.5" />
                          IUs
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ReleaseIUModal */}
      {showReleaseModal && (
        <ReleaseIUModal
          isOpen={showReleaseModal}
          onClose={() => setShowReleaseModal(false)}
          project={project}
          task={task}
          submission={releaseSubmission}
          onRelease={handleIUsReleased}
          getExplorerUrl={getExplorerUrl}
        />
      )}

      {/* Form modals */}
      {showAppForm && task && (
        <TaskApplicationForm
          task={task}
          onClose={() => {
            setShowAppForm(false);
          }}
          onSubmit={async (payload) => {
            try {
              await taskAPI.applyForTask(task._id, payload);
              toast.success("Application submitted successfully!");
              setShowAppForm(false);
              onTaskUpdated(project._id);
            } catch (error) {
              console.error("Error submitting application:", error);
              toast.error("Failed to submit application. Please try again.");
            }
          }}
        />
      )}

      {appsModal && (
        <TaskApplicationsView
          task={task}
          onClose={() => {
            setAppsModal(false);
            setSelectedApplication(null);
          }}
          onRespond={async (applicationId, status, feedback) => {
            setLoading(true);
            setProcessingApplicationId(applicationId);
            try {
              if (status === "approved") {
                await handleApprove(applicationId);
              } else if (status === "rejected") {
                await handleReject(applicationId);
              }
              
              // Refresh the task data after response
              onTaskUpdated(project._id);
              
              // Close the modal
              setAppsModal(false);
              setSelectedApplication(null);
            } catch (error) {
              console.error(`Error ${status === "approved" ? "approving" : "rejecting"} application:`, error);
              toast.error(`Failed to ${status === "approved" ? "approve" : "reject"} application.`);
            } finally {
              setLoading(false);
              setProcessingApplicationId(null);
            }
          }}
          isCreator={isCreator}
          selectedApplication={selectedApplication}
          setSelectedApplication={setSelectedApplication}
        />
      )}

      {subsModal && (
        <TaskSubmissionsView
          task={task}
          project={project}
          onClose={() => {
            setSubsModal(false);
            setSelectedSubmission(null);
          }}
          onReview={async (subId, status, feedback) => {
            // Review the submission
            await taskAPI.reviewSubmission(subId, { status, feedback });
            
            // If a submission is approved, update task status to "approved"
            if (status === "approved") {
              await taskAPI.update(task._id, { status: "approved" });
            }
            
            onTaskUpdated(project._id);
          }}
          onReleaseIUs={handleReleaseIUs}
          isCreator={isCreator}
          selectedSubmission={selectedSubmission}
          setSelectedSubmission={setSelectedSubmission}
          getExplorerUrl={getExplorerUrl}
        />
      )}
      
      {showSubmitForm && task && (
        <TaskSubmissionForm
          task={task}
          onClose={() => {
            setShowSubmitForm(false);
            onTaskUpdated(project._id);
          }}
          onSubmit={async (payload) => {
            try {
              // Submit work
              await taskAPI.submitWork(task._id, payload);
              
              // Update task status based on permission type
              if (task.permissionType === "multiple") {
                // For multiple submissions type, always ensure it's in "submitted" status
                // so other users can continue to submit work
                if (task.status !== "submitted") {
                  await taskAPI.update(task._id, { status: "submitted" });
                }
              } else {
                // For other types (assign, claim, application) move to submitted
                if (task.status !== "submitted") {
                  await taskAPI.update(task._id, { status: "submitted" });
                }
              }
              
              toast.success("Work submitted successfully!");
              setShowSubmitForm(false);
              onTaskUpdated(project._id);
            } catch (error) {
              console.error("Error submitting work:", error);
              toast.error("Failed to submit work. Please try again.");
            }
          }}
        />
      )}
    </Drawer>
  );
}
